import type { DocumentModel } from '@ctw/shared/api/fhir/models/document';
import type {
  FilterItem,
  FilterOptionCheckbox,
} from '@ctw/shared/components/filter-bar/filter-bar-types';
import { dismissFilter } from '@ctw/shared/content/resource/filters';
import type { PredicateMap } from '@ctw/shared/hooks/use-filtered-sorted-data';
import { getLoincDisplay } from '@ctw/shared/utils/loinc';
import { compact, uniq } from 'lodash-es';

export const ZUS_CREATION_DATE_URL = 'https://zusapi.com/created-at';

const fileTypeMap: Record<string, string> = {
  'application/xml': 'CDA',
  'application/pdf': 'PDF',
  'image/png': 'PNG',
  'image/jpeg': 'JPEG',
  'image/tiff': 'TIFF',
};

export function documentsFilter(
  documents: Array<DocumentModel> = [],
  hasDocumentTypeFilters = false,
): Array<FilterItem> {
  const filters: Array<FilterItem> = [
    dismissFilter,
    {
      key: 'contentType',
      display: 'File Type',
      type: 'checkbox',
      values: compact(
        uniq(documents.map((d) => d.contentType).filter((c) => c && fileTypeMap[c])),
      ).map((fileType) => ({
        name: fileTypeMap[fileType],
        key: fileType,
      })),
    },
  ];

  if (hasDocumentTypeFilters) {
    const typeCodeMap = new Map<string, string>();
    const contentCodeMap = new Map<string, string>();
    for (const doc of documents) {
      if (doc.loincTypeCode && !typeCodeMap.has(doc.loincTypeCode) && doc.typeDisplay) {
        typeCodeMap.set(doc.loincTypeCode, doc.typeDisplay);
      }
      for (const category of doc.category) {
        const categoryCode = category.coding?.[0]?.code;
        if (categoryCode && !contentCodeMap.has(categoryCode)) {
          const loincDisplay = getLoincDisplay(category);
          if (loincDisplay) {
            contentCodeMap.set(categoryCode, loincDisplay);
          }
        }
      }
    }
    filters.push({
      key: 'typeCode',
      display: 'Type',
      type: 'checkbox',
      values: Array.from(typeCodeMap.entries())
        .map(([code, display]) => ({
          name: display,
          key: code,
        }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    });
    filters.push({
      key: 'category',
      display: 'CDA Sections',
      type: 'checkbox',
      values: Array.from(contentCodeMap.entries())
        .map(([code, display]) => ({
          name: display,
          key: code,
        }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    });
  }

  return filters;
}

export const documentFilterPredicates: PredicateMap<FilterOptionCheckbox['predicate']> = {
  category: (values, item) => {
    return values.some((value) =>
      (item as DocumentModel).category.some((category) => category.coding?.[0]?.code === value),
    );
  },
};
